import { saveAs } from 'file-saver';

export interface IGpthelpExportMeta {
  kind: 'xlsx';
  sheetCount: number;
  sheets: Array<{
    name: string;
    rows: number;
    columns: number;
  }>;
}

interface IExportEntry {
  id: string;
  createdAt: number;
  filename: string;
  mime: string;
  bytes: Uint8Array;
  meta: IGpthelpExportMeta;
}

/** A report waits this long for its «Descargar» (it lives in this tab only). */
const TTL_MS = 30 * 60 * 1000;
const MAX_ENTRIES = 10;

const registry = new Map<string, IExportEntry>();

function nowMs() {
  return Date.now();
}

function generateId() {
  // No new deps; sufficiently unique for in-memory registry
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function cleanupExpired() {
  const now = nowMs();
  for (const [id, entry] of registry) {
    if (now - entry.createdAt > TTL_MS) {
      registry.delete(id);
    }
  }
}

function evictIfNeeded() {
  if (registry.size <= MAX_ENTRIES) return;

  const entries = Array.from(registry.values()).sort((a, b) => a.createdAt - b.createdAt);
  const over = registry.size - MAX_ENTRIES;
  for (let i = 0; i < over; i++) {
    registry.delete(entries[i]!.id);
  }
}

export function registerExport(params: {
  filename: string;
  mime: string;
  bytes: Uint8Array;
  meta: IGpthelpExportMeta;
}): string {
  cleanupExpired();

  const id = generateId();
  registry.set(id, {
    id,
    createdAt: nowMs(),
    filename: params.filename,
    mime: params.mime,
    bytes: params.bytes,
    meta: params.meta,
  });

  // Enforce cap after insertion to avoid MAX_ENTRIES+1 drift.
  evictIfNeeded();

  return id;
}

export function getExportMeta(exportId: string): {
  filename: string;
  meta: IGpthelpExportMeta;
} {
  cleanupExpired();

  const entry = registry.get(exportId);
  if (!entry) {
    throw new Error('Export expired or not found');
  }

  return {
    filename: entry.filename,
    meta: entry.meta,
  };
}

/**
 * Saves the report. It stays until it expires, so a save that did nothing
 * visible (an app WebView) can be tried again. False when it expired or was
 * made before a reload or in another tab (the registry lives in memory).
 */
export function downloadExport(exportId: string): boolean {
  cleanupExpired();

  const entry = registry.get(exportId);
  if (!entry) return false;

  const blob = new Blob([entry.bytes], { type: entry.mime });
  saveAs(blob, entry.filename);
  return true;
}

